/**
 * Frido Kids Size Table runtime — the inline radio size table (basis column +
 * measurement columns from the structured chart, e.g. Age Group | Foot Length
 * in CM | Foot Size in EU) with a finder button that opens the Size Guide
 * drawer and a caption row. One layout by contract; the pills grid is
 * fszfrd01.
 *
 * Requires the structured chart (spectrum.size_chart_live -> chart_data) in
 * the pool — without it the table renders nothing (fail visible, fix data).
 * Picking a row selects the matching variant (?variant= + event).
 */
;(() => {
  if (window.__sai_fszfrd02_initialized__) return
  window.__sai_fszfrd02_initialized__ = true

  const SNIPPET_ID = 'fszfrd02'
  const TAG = 'sai-fszfrd02'
  const FEATURE_SLUG = 'variant_picker'

  function esc(s) {
    return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }
  function arrowSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/></svg>'
  }
  function closeSvg() {
    return '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>'
  }
  function readPool(node) {
    for (let i = 0; i < node.children.length; i++) {
      const c = node.children[i]
      if (c.tagName === 'SCRIPT' && c.hasAttribute('data-spectrum-snippet-pool')) {
        try { return JSON.parse(c.textContent) } catch (e) { return {} }
      }
    }
    return {}
  }

  function optKey(opt) { return 'option' + (opt ? opt.position : 1) }
  function variantsFor(pool, selections) {
    return (pool.product.variants || []).filter((v) =>
      Object.entries(selections).every(([k, val]) => !val || v[k] === val))
  }
  function isAvailable(pool, selections) { return variantsFor(pool, selections).some((v) => v.available) }
  function pickVariant(pool, selections) {
    const vs = variantsFor(pool, selections)
    return vs.find((v) => v.available) || vs[0] || null
  }

  const El = class extends HTMLElement {
    connectedCallback() {
      if (this._init) return
      this._init = true
      this._track = () => {}
    }
    setAnalytics(t) { this._track = typeof t === 'function' ? t : () => {} }

    applyVariant(_content, pool) {
      this._pool = pool && typeof pool === 'object' ? pool : {}
      const p = this._pool.product
      if (!p || !p.size_option) return
      if (!(this._pool.size_chart && (this._pool.size_chart.sizes || []).length)) return
      this._selected = p.size_option.selected || null
      this._render()
      this._bind()
    }

    _props() { return (this._pool && this._pool.props) || {} }

    _selections(value) {
      return { [optKey(this._pool.product.size_option)]: value }
    }

    _sizeTable() {
      const p = this._props()
      const opt = this._pool.product.size_option
      const chart = this._pool.size_chart
      const rows = (chart && chart.sizes) || []
      const byLabel = {}
      for (const s of rows) byLabel[s.label] = s
      const cols = rows.length && rows[0].measurements ? rows[0].measurements.map((m) => m.name) : []
      const finder = p.finder_text
        ? `<button type="button" class="sai-fszfrd02__finder" data-action="size-guide">${esc(p.finder_text)}<span class="sai-fszfrd02__finder-arrow">${arrowSvg()}</span></button>` : ''
      const caption = p.caption ? `<div class="sai-fszfrd02__caption">${esc(p.caption)}</div>` : ''
      let thead = `<div class="sai-fszfrd02__trow sai-fszfrd02__thead"><div class="sai-fszfrd02__tcell sai-fszfrd02__tcell--label">${esc((chart && chart.basisAttribute) || opt.name)}</div>`
      for (const c of cols) thead += `<div class="sai-fszfrd02__tcell">${esc(c)}</div>`
      thead += '</div>'
      // oos_display prop (Studio): strike (default) | hide | disable. This table is JS-only (no SSR row).
      const oosMode = this._props().oos_display || 'strike'
      let trs = ''
      for (const v of opt.values) {
        const avail = isAvailable(this._pool, this._selections(v))
        if (!avail && oosMode === 'hide') continue
        const disabled = !avail && oosMode === 'disable'
        const sel = v === this._selected
        const s = byLabel[v]
        let cells = `<div class="sai-fszfrd02__tcell sai-fszfrd02__tcell--label"><span class="sai-fszfrd02__radio" data-on="${sel}"></span>${esc(v)}</div>`
        for (const c of cols) {
          const m = s ? (s.measurements || []).find((x) => x.name === c) : null
          cells += `<div class="sai-fszfrd02__tcell">${esc(m ? m.value : '')}</div>`
        }
        trs += `<div class="sai-fszfrd02__trow${sel ? ' is-sel' : ''}${avail ? '' : ' is-oos'}${disabled ? ' is-disabled' : ''}"${disabled ? ' aria-disabled="true"' : ' data-action="pick"'} data-value="${esc(v)}" role="radio" aria-checked="${sel}">${cells}</div>`
      }
      return `${finder}${caption}<div class="sai-fszfrd02__table" role="radiogroup">${thead}${trs}</div>`
    }

    // Guide-media illustration images from the pool (the how-to-measure art).
    _guideImages() {
      return ((this._pool && this._pool.guide_media) || []).filter((m) => m && m.url)
    }

    // A minimal image-only modal — no size table, no swatches, no CTA. Renders
    // just the guide illustration(s) + close. Empty string when no guide media.
    _measureModal() {
      const imgs = this._guideImages()
      if (!imgs.length) return ''
      const body = imgs.map((m) => m.type === 'video'
        ? `<video class="sai-fszfrd02__mimg" src="${esc(m.url)}"${m.poster ? ` poster="${esc(m.poster)}"` : ''} controls playsinline preload="none"></video>`
        : `<img class="sai-fszfrd02__mimg" src="${esc(m.url)}" alt="${esc(m.alt || '')}" loading="lazy" decoding="async">`).join('')
      const title = this._props().measure_title
      const heading = title ? `<div class="sai-fszfrd02__measure-title">${esc(title)}</div>` : ''
      return `<div class="sai-fszfrd02__measure" data-open="false" aria-hidden="true">` +
        `<div class="sai-fszfrd02__measure-backdrop" data-action="measure-close"></div>` +
        `<div class="sai-fszfrd02__measure-panel" role="dialog" aria-modal="true">` +
        `<button type="button" class="sai-fszfrd02__measure-close" data-action="measure-close" aria-label="Close">${closeSvg()}</button>` +
        `${heading}<div class="sai-fszfrd02__measure-body">${body}</div></div></div>`
    }

    _render() {
      const p = this._props()
      const label = p.label_prefix
        ? `<div class="sai-fszfrd02__label"><span class="sai-fszfrd02__label-key">${esc(p.label_prefix)}</span><span class="sai-fszfrd02__label-val" data-sel-label>${esc(this._selected || '')}</span></div>` : ''
      this.innerHTML = `<div class="sai-fszfrd02__inner">${label}${this._sizeTable()}</div>${this._measureModal()}`
    }

    _openMeasure() {
      const m = this.querySelector('.sai-fszfrd02__measure')
      if (!m) return
      m.dataset.open = 'true'; m.setAttribute('aria-hidden', 'false')
      this._prevOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }
    _closeMeasure() {
      const m = this.querySelector('.sai-fszfrd02__measure')
      if (!m) return
      m.dataset.open = 'false'; m.setAttribute('aria-hidden', 'true')
      document.body.style.overflow = this._prevOverflow ?? ''
    }

    _bind() {
      if (this._clickBound) return
      this._clickBound = true
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-action]')
        if (!t) return
        if (t.dataset.action === 'pick') return this._pick(t.dataset.value)
        if (t.dataset.action === 'measure-close') return this._closeMeasure()
        if (t.dataset.action === 'size-guide') {
          // Self-contained: if the how-to-measure illustration is authored,
          // open the local image modal. Otherwise fall back to the shared
          // size-guide drawer (so other placements keep working).
          if (this._guideImages().length) this._openMeasure()
          else window.dispatchEvent(new CustomEvent('spectrum:size-guide-open', {}))
          this._track(`${FEATURE_SLUG}:size_guide_open_elem_clicked`, {})
        }
      })
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this._closeMeasure() })
    }

    _pick(value) {
      if (value === this._selected) return
      this._selected = value
      const variant = pickVariant(this._pool, this._selections(value))
      if (variant) {
        try {
          const url = new URL(window.location.href)
          url.searchParams.set('variant', String(variant.id))
          window.history.replaceState({}, '', url.toString())
        } catch (e) {}
        window.dispatchEvent(new CustomEvent('spectrum:variant-change', { detail: { variantId: variant.id } }))
        // Sync the theme product form's hidden variant input. This snippet
        // replaces the native variant picker, and product-form.js submits
        // whatever is in `[name="id"]` — without this, add-to-cart uses the
        // default variant regardless of the pick. Scope to forms whose current
        // value is one of THIS product's variants so upsell forms are untouched.
        try {
          const _vids = {}
          ;(this._pool.product.variants || []).forEach((vv) => {
            _vids[String(vv.id)] = 1
          })
          document.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
            if (_vids[String(input.value)]) {
              input.value = String(variant.id)
              input.dispatchEvent(new Event('change', { bubbles: true }))
            }
          })
        } catch (e) {}
      }
      this._render()
      this._track(`${FEATURE_SLUG}:select_size`, { value })
    }
  }

  if (!customElements.get(TAG)) customElements.define(TAG, El)

  function bindAll() {
    const api = window.__spectrumAi && window.__spectrumAi.snippet
    for (const node of document.querySelectorAll(`[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`)) {
      const el = node.querySelector(TAG)
      if (!el) continue
      // First paint straight from the server-rendered pool — the pool's props
      // are the instance's default config, so rendering never waits on the
      // SDK (which may load after DOMContentLoaded or resolve asynchronously).
      if (!el._painted) {
        const initPool = readPool(node)
        if (initPool && (initPool.product || initPool.bundle || initPool.axis || initPool.buying_options)) { el._painted = true; el.applyVariant({}, initPool) }
      }
      if (api && typeof api.bind === 'function' && !el._wrapBound) {
        el._wrapBound = true
        const handles = api.bind(node, (ctx) => {
          const e = node.querySelector(TAG)
          if (e) e.applyVariant(ctx.variant ? ctx.variant.content : {}, readPool(node))
        })
        if (handles) el.setAnalytics(handles.track)
      }
    }
    for (const el of document.querySelectorAll(TAG)) {
      if (el._applied || el._wrapBound || el.closest('[data-spectrum-instance-id]')) continue
      const parent = el.closest('[data-spectrum-lq-snippet]') || el.parentElement
      if (!parent) continue
      const pool = readPool(parent)
      if (pool && pool.product) { el._applied = true; el.applyVariant({}, pool) }
    }
  }

  function schedule() {
    bindAll()
    // the SDK can load after us — retry until it binds (or give up quietly)
    let tries = 0
    const t = setInterval(() => {
      bindAll()
      const api = window.__spectrumAi && window.__spectrumAi.snippet
      if ((api && typeof api.bind === 'function') || ++tries > 20) clearInterval(t)
    }, 500)
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true })
  } else {
    schedule()
  }

  // The storefront SDK can re-mount slot wrappers after load (conflict
  // resolution / vis flips) — a replaced wrapper holds a fresh unpainted
  // element, so rebind on DOM changes, debounced.
  const __saiRelevant = (records) => {
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n.nodeType !== 1) continue
        if ((n.matches && n.matches(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"], [data-spectrum-lq-snippet]`)) ||
            (n.querySelector && n.querySelector(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"]`))) return true
      }
    }
    return false
  }
  const __saiMo = new MutationObserver((records) => {
    if (__saiMo._t || !__saiRelevant(records)) return
    __saiMo._t = setTimeout(() => { __saiMo._t = null; bindAll() }, 150)
  })
  __saiMo.observe(document.documentElement, { childList: true, subtree: true })
  setTimeout(function () { try { __saiMo.disconnect() } catch (e) {} }, 30000)
})()
